/**
 * Post-decrypt URL allowlists for media the ciphertext can name (GIFs,
 * custom emoji). Search/API sanitisation is not enough: a malicious member
 * can put an arbitrary https URL in the payload.
 *
 * Duplicate of the API Klipy sanitiser — the UI package must not import
 * `apps/api`. Do not reuse the embed allowlist here.
 */

const KLIPY_CDN_HOSTNAME = 'static.klipy.com';

/**
 * Validates a Klipy CDN URL and strips query parameters / fragments.
 * Returns `undefined` for any URL that does not point at the allowed host.
 */
export function sanitiseKlipyCdnUrl(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  try {
    const parsed = new URL(raw);
    if (parsed.hostname !== KLIPY_CDN_HOSTNAME || parsed.protocol !== 'https:') {
      return undefined;
    }
    return `https://${KLIPY_CDN_HOSTNAME}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

/**
 * GIF blur placeholders are applied as CSS `url(...)`. Only data-image
 * URLs without characters that could break out of `url()` are allowed.
 */
export function isAllowedGifBlurPreview(raw: string): boolean {
  if (!raw.startsWith('data:image/')) return false;
  if (raw.startsWith('data:image/svg')) return false;
  if (/[)"'\s]/.test(raw)) return false;
  return true;
}

/**
 * Custom-emoji `img src` may only load from this instance's media CDN.
 * Missing or unparseable `mediaOrigin` fails closed.
 */
export function isAllowedCustomEmojiUrl(
  raw: string,
  mediaOrigin: string | undefined | null,
): boolean {
  if (!mediaOrigin) return false;
  try {
    const url = new URL(raw);
    const origin = new URL(mediaOrigin);
    return url.protocol === 'https:' && url.origin === origin.origin;
  } catch {
    return false;
  }
}
